import React, { useState, useEffect } from "react";
import { Container, Row, Col, Table, Form, InputGroup } from "react-bootstrap";
import { FaSearch, FaTrash } from "react-icons/fa";
import { collection, getDocs, doc, updateDoc, deleteDoc } from "firebase/firestore";
import { db } from "../firebase";
import PaginationControl from "../calculations/PaginationControl";
import AddCountryModal from "./AddCountryModal";

const CountriesTable = () => {
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortOrder, setSortOrder] = useState("a-z"); // 'a-z', 'z-a', 'enabled-first'
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const fetchCountries = async () => {
    try {
      setLoading(true);
      const querySnapshot = await getDocs(collection(db, "countries"));
      const list = querySnapshot.docs.map((docSnap) => ({
        docId: docSnap.id,
        ...docSnap.data(),
      }));
      setCountries(list);
    } catch (error) {
      console.error("Error fetching countries:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCountries();
  }, []);

  const handleToggleEnable = async (docId, currentStatus) => {
    try {
      const countryRef = doc(db, "countries", docId);
      const newStatus = !currentStatus;
      await updateDoc(countryRef, { enabled: newStatus });
      setCountries((prev) =>
        prev.map((c) => (c.docId === docId ? { ...c, enabled: newStatus } : c))
      );
    } catch (error) {
      console.error("Error toggling country status:", error);
    }
  };

  const handleDeleteCountry = async (docId, name) => {
    if (window.confirm(`Are you sure you want to delete ${name}?`)) {
      try {
        await deleteDoc(doc(db, "countries", docId));
        setCountries((prev) => prev.filter((c) => c.docId !== docId));
      } catch (error) {
        console.error("Error deleting country:", error);
      }
    }
  };

  // Search filter
  const filteredCountries = countries.filter((c) => {
    const nameMatch = c.name?.toLowerCase().includes(searchQuery.toLowerCase());
    const codeMatch = c.code?.toLowerCase().includes(searchQuery.toLowerCase());
    return nameMatch || codeMatch;
  });

  // Sort logic
  const sortedCountries = [...filteredCountries].sort((a, b) => {
    if (sortOrder === "a-z") {
      return (a.name || "").localeCompare(b.name || "");
    } else if (sortOrder === "z-a") {
      return (b.name || "").localeCompare(a.name || "");
    } else if (sortOrder === "enabled-first") {
      return (b.enabled ? 1 : 0) - (a.enabled ? 1 : 0);
    }
    return 0;
  });

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentCountries = sortedCountries.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(sortedCountries.length / itemsPerPage);

  return (
    <section className="px-1 py-4 nav-body-bg btm-rad">
      <AddCountryModal onAddSuccess={fetchCountries} />

      <Container>
        {/* Header and Controls */}
        <Row className="align-items-center justify-content-between flex-column flex-md-row text-center text-md-start mb-4">
          <Col xs="auto" className="mb-3 mb-md-0">
            <h3 className="text-h2">All Countries</h3>
          </Col>

          {/* Search Box */}
          <Col md={4} className="mb-3 mb-md-0">
            <InputGroup className="theme-search-group">
              <Form.Control
                type="text"
                placeholder="Search countries"
                className="db-set-style border-0 shadow-none"
                style={{ backgroundColor: "transparent" }}
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
              />
              <InputGroup.Text className="bg-transparent border-0">
                <FaSearch style={{ color: "var(--accent-lime)" }} />
              </InputGroup.Text>
            </InputGroup>
          </Col>

          {/* Sorting and Add Button */}
          <Col xs="auto" className="d-flex align-items-center gap-2 justify-content-center">
            <select
              className="form-select theme-sort-select"
              style={{ width: "120px", padding: "3px 0px" }}
              value={sortOrder}
              onChange={(e) => {
                setSortOrder(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="a-z">Name (A-Z)</option>
              <option value="z-a">Name (Z-A)</option>
              <option value="enabled-first">Enabled First</option>
            </select>

            <button
              className="btn btn-primary px-3"
              style={{ padding: "3px 0px" }}
              data-bs-toggle="modal"
              data-bs-target="#addCountryModal"
            >
              + Add Country
            </button>
          </Col>
        </Row>

        {/* Table View */}
        <div
          className="table-responsive scrollable-table-y mt-2 bg-transparent"
          style={{ minHeight: "60vh", overflowY: "auto" }}
        >
          <style>
            {`
              @media (max-width: 768px) {
                .scrollable-table-y {
                  max-height: 400px;
                  overflow-y: auto;
                }
              }
              .form-switch .form-check-input {
                width: 2.5em;
                height: 1.25em;
                cursor: pointer;
              }
            `}
          </style>

          <Table className="table-hover align-middle body-text-two mb-0">
            <thead className="sticky-top top-0" style={{ zIndex: 10 }}>
              <tr>
                <th className="transparent-header text-center text-white fw-bold">SL</th>
                <th className="transparent-header text-white fw-bold">Country Name</th>
                <th className="transparent-header text-center text-white fw-bold">Country Code</th>
                <th className="transparent-header text-center text-white fw-bold">Dial Code</th>
                <th className="transparent-header text-center text-white fw-bold">Status</th>
                <th className="transparent-header text-center text-white fw-bold">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="6" className="text-center py-5 transparent-header">
                    <div className="admin-loader"></div>
                  </td>
                </tr>
              ) : currentCountries.length > 0 ? (
                currentCountries.map((c, index) => (
                  <tr key={c.docId || index} style={{ borderBottom: "1px solid var(--divider)" }}>
                    <td className="py-3 transparent-header text-center text-white">
                      {indexOfFirstItem + index + 1}
                    </td>
                    <td className="py-3 transparent-header text-white fw-semibold">
                      {c.name}
                    </td>
                    <td className="py-3 transparent-header text-center text-white font-monospace">
                      {c.code}
                    </td>
                    <td className="py-3 transparent-header text-center text-white font-monospace">
                      {c.dialCode || "-"}
                    </td>
                     <td className="py-3 transparent-header text-center">
                       <div className="d-flex align-items-center justify-content-center gap-2">
                         <div className="form-check form-switch mb-0">
                           <input
                             className="form-check-input"
                             type="checkbox"
                             role="switch"
                             checked={c.enabled ?? true}
                             onChange={() => handleToggleEnable(c.docId, c.enabled ?? true)}
                             style={{ margin: 0 }}
                           />
                         </div>
                         <span
                           className={`fw-semibold small ${
                             c.enabled ?? true ? "text-success" : "text-danger"
                           }`}
                           style={{ width: "65px", textAlign: "left" }}
                         >
                           {c.enabled ?? true ? "Enabled" : "Disabled"}
                         </span>
                       </div>
                     </td>
                    <td className="py-3 transparent-header text-center">
                      <button
                        className="btn btn-link text-danger p-0"
                        style={{ outline: "none", boxShadow: "none" }}
                        onClick={() => handleDeleteCountry(c.docId, c.name)}
                        title="Delete Country"
                      >
                        <FaTrash size={16} />
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="text-center py-5 transparent-header text-white">
                    No countries found. Add a country to get started!
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>

        {/* Pagination Controls */}
        {!loading && totalPages > 1 && (
          <div className="d-flex justify-content-center mt-4 pb-3">
            <PaginationControl
              totalPages={totalPages}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
            />
          </div>
        )}
      </Container>
    </section>
  );
};

export default CountriesTable;
